import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
    Grid,
    Typography,
} from '@mui/material';
import { addDoc, collection, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useObras } from '../../context/ObrasContext';
import { useAuth } from '../../context/AuthContext';
import { Herramienta } from '../../types';

interface AddHerramientaModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    herramientaToEdit?: Herramienta | null;
}

const AddHerramientaModal: React.FC<AddHerramientaModalProps> = ({ open, onClose, onSuccess, herramientaToEdit }) => {
    const { obras, obraSeleccionada } = useObras();
    const { usuario } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        nombre: '',
        obraId: '',
        descripcion: '',
        tipo: 'nuestra' as 'nuestra' | 'alquilada',
        duracionAlquilerDias: 30,
        fechaInicioAlquiler: new Date().toISOString().split('T')[0],
        cantidad: 1
    });

    // Update formData when opening
    React.useEffect(() => {
        if (open) {
            if (herramientaToEdit) {
                let fechaInicioStr = new Date().toISOString().split('T')[0];
                if (herramientaToEdit.fechaInicioAlquiler) {
                    const dateObj = herramientaToEdit.fechaInicioAlquiler.toDate 
                        ? herramientaToEdit.fechaInicioAlquiler.toDate() 
                        : new Date(herramientaToEdit.fechaInicioAlquiler);
                    if (!isNaN(dateObj.getTime())) {
                        fechaInicioStr = dateObj.toISOString().split('T')[0];
                    }
                }

                setFormData({
                    nombre: herramientaToEdit.nombre || '',
                    obraId: herramientaToEdit.obraId || '',
                    descripcion: herramientaToEdit.descripcion || '',
                    tipo: herramientaToEdit.tipo || 'nuestra',
                    duracionAlquilerDias: herramientaToEdit.duracionAlquilerDias || 30,
                    fechaInicioAlquiler: fechaInicioStr,
                    cantidad: herramientaToEdit.cantidad || 1
                });
            } else {
                // Find ByB to use as default if no specific obra is selected
                const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');

                setFormData({
                    nombre: '',
                    obraId: obraSeleccionada || obraByB?.id || '',
                    descripcion: '',
                    tipo: 'nuestra',
                    duracionAlquilerDias: 30,
                    fechaInicioAlquiler: new Date().toISOString().split('T')[0],
                    cantidad: 1
                });
            }
        }
    }, [open, herramientaToEdit, obraSeleccionada, obras]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: unknown } }) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.nombre) {
            setError('El nombre es obligatorio');
            return;
        }
        if (!formData.obraId) {
            setError('Debe seleccionar una Ubicación/Obra');
            return;
        }

        setLoading(true);
        setError(null);

        const selectedObra = obras.find(o => o.id === formData.obraId);

        try {
            const dataToSave: any = {
                nombre: formData.nombre,
                descripcion: formData.descripcion,
                obraId: formData.obraId,
                obraNombre: selectedObra?.nombre || 'Desconocida',
                tipo: formData.tipo,
                cantidad: Number(formData.cantidad) || 1
            };

            if (formData.tipo === 'alquilada') {
                const startDate = new Date(formData.fechaInicioAlquiler);
                startDate.setHours(12, 0, 0, 0);

                dataToSave.fechaInicioAlquiler = Timestamp.fromDate(startDate);
                dataToSave.duracionAlquilerDias = Number(formData.duracionAlquilerDias);
            } else {
                dataToSave.fechaInicioAlquiler = null;
                dataToSave.duracionAlquilerDias = null;
            }

            if (herramientaToEdit) {
                // Update existing
                const docRef = doc(db, 'herramientas', herramientaToEdit.id);
                await updateDoc(docRef, dataToSave);
            } else {
                // Create new
                const nuevaHerramienta: Omit<Herramienta, 'id'> = {
                    ...dataToSave,
                    fechaAsignacion: Timestamp.now(),
                    asignadaPor: usuario?.email || 'unknown',
                    usuarioEmail: usuario?.email || undefined,
                    estado: 'en_uso',
                };
                await addDoc(collection(db, 'herramientas'), nuevaHerramienta);
            }

            onSuccess();
            onClose();
            // Restablecer form
            const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
            setFormData({
                nombre: '',
                obraId: obraSeleccionada || obraByB?.id || '',
                descripcion: '',
                tipo: 'nuestra',
                duracionAlquilerDias: 30,
                fechaInicioAlquiler: new Date().toISOString().split('T')[0],
                cantidad: 1
            });
        } catch (err) {
            console.error('Error al guardar herramienta:', err);
            setError('Error al guardar la herramienta. Intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {herramientaToEdit ? 'Editar Herramienta' : 'Nueva Herramienta'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        label="Nombre de la Herramienta"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        autoFocus
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <TextField
                                label="Cantidad"
                                type="number"
                                name="cantidad"
                                value={formData.cantidad}
                                onChange={handleChange}
                                fullWidth
                                required
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                    name="tipo"
                                    value={formData.tipo}
                                    label="Tipo"
                                    onChange={(e) => handleChange(e as any)}
                                >
                                    <MenuItem value="nuestra">Propia</MenuItem>
                                    <MenuItem value="alquilada">Alquilada</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>Ubicación Inicial</InputLabel>
                                <Select
                                    name="obraId"
                                    value={formData.obraId}
                                    label="Ubicación Inicial"
                                    onChange={(e) => handleChange(e as any)}
                                >
                                    {obras
                                        .filter(o => o.estado === 'activa')
                                        // Sort ByB to top
                                        .sort((a, b) => {
                                            const isAByB = a.nombre.toUpperCase() === 'BYB' || a.nombre.toUpperCase() === 'B&B';
                                            const isBByB = b.nombre.toUpperCase() === 'BYB' || b.nombre.toUpperCase() === 'B&B';
                                            if (isAByB) return -1;
                                            if (isBByB) return 1;
                                            return a.nombre.localeCompare(b.nombre);
                                        })
                                        .map((obra) => (
                                            <MenuItem key={obra.id} value={obra.id}>
                                                {obra.nombre}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {formData.tipo === 'alquilada' && (
                        <Box sx={{ border: '1px dashed', borderColor: 'primary.main', p: 2, borderRadius: 2, bgcolor: 'primary.50' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Detalles del Alquiler
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Fecha Inicio"
                                        type="date"
                                        name="fechaInicioAlquiler"
                                        value={formData.fechaInicioAlquiler}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Duración (días)"
                                        type="number"
                                        name="duracionAlquilerDias"
                                        value={formData.duracionAlquilerDias}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <TextField
                        label="Descripción / Detalles"
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Número de serie, marca, estado..."
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? 'Guardando...' : (herramientaToEdit ? 'Guardar Cambios' : 'Crear Herramienta')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddHerramientaModal;
